import { BaseControl } from "../controls";
import { BaseWidget } from "../widgets";
import { useMemo } from "react";
import { ControlLoadGate, ControlsAccordionMenu, GroupedControlRenderer } from "../controls/controlPrimitives";

// Re-export so existing callers (e.g. `import { ControlsAccordionMenu } from "./WidgetControl"`)
// keep working.
export { ControlsAccordionMenu, GroupedControlRenderer };

/**
 * Panel for rendering controls of a widget. 
 * 
 * It separates grouped controls (with children) from non-grouped controls. 
 * Grouped controls are rendered in their own accordion sections, while
 * non-grouped controls are placed together in an "Overig" section.
 * 
 * @param {object} props
 * @param {BaseWidget} props.widget Widget instance
 * @param {(key: string, value: any) => void} props.onChange onChange handler
 * @param {number} [props.flashToken] Bumped on every (re)select; restarts the flash overlay without rebuilding the controls
 * @returns 
 */
export const WidgetControlsPanel = ({ widget, onChange, flashToken = 0 }) => {

    /* Stable instances per widget id. useLiveReportWidgets deserialises a NEW
       widget object on every snapshot, and a snapshot follows every autosave —
       keying on the object rebuilt the controls and re-ran their async
       `options()` (all Outputs + Activities for an Output widget) on each
       setting change. getControls() reads no settings (only Container reads
       `this.parent`, which is fixed per id), so the first instance stays valid. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const controls = useMemo(() => widget.getControls(), [widget.id]);

    const allControls = Object.entries(controls);

    const nonGroupedControls = Object.fromEntries(allControls.filter(([_, control]) => 
        Object.keys(control.children).length === 0
    ));
    const groupedControls = Object.fromEntries(allControls.filter(([_, control]) => 
        Object.keys(control.children).length > 0
    ));

    return (
        <div className="widget-controls-panel">
            {/* Keyed overlay: a new token remounts only this div, so the CSS
                animation replays while the controls (and their loaded
                options) stay mounted. */}
            <div key={flashToken} className="widget-controls-flash" data-testid="widget-controls-flash" aria-hidden="true" />
            {Object.entries(nonGroupedControls).length > 0 &&
                <ControlsAccordionMenu label={'Basis instellingen'} isOpen={true}>
                    {Object.entries(nonGroupedControls).map(([key, control]) => (
                        <ControlRenderer 
                            key={key}
                            widget={widget}
                            control={control}
                            controlKey={key}
                            onChange={onChange}
                        />
                    ))}
                </ControlsAccordionMenu>
            }
            {Object.entries(groupedControls).map(([key, control]) => (
                <ControlRenderer 
                    key={key}
                    widget={widget}
                    control={control}
                    controlKey={key}
                    onChange={onChange}
                />
            ))}
        </div>
    );
}

/**
 * Renders a single control for a widget. 
 * 
 * @param {object} props
 * @param {BaseWidget} props.widget Widget instance
 * @param {BaseControl} props.control Control object
 * @param {string} props.controlKey Control key within the widget (see BaseWidget.getControls())
 * @param {(key: string, value: any) => void} props.onChange onChange handler
 */
export const ControlRenderer = ({ widget, control, controlKey, onChange }) => (
    <ControlLoadGate control={control}>
        {() => {
            /* Optional visibility guard — re-evaluated on every render so it can depend
               on sibling settings (e.g. only show number-styling controls when the
               Output widget's graphType is 'number'). */
            if (typeof control.visibleWhen === 'function' && !control.visibleWhen(widget)) {
                return null;
            }

            return control.render(
                widget.getSetting(controlKey),
                (newValue) => onChange(controlKey, newValue),
                widget
            );
        }}
    </ControlLoadGate>
);


// `GroupedControlRenderer` and `ControlsAccordionMenu` now live in
// `controls/controlPrimitives.jsx` and are re-exported above.

